import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Target, Plus, Inbox, TrendingUp, BarChart3, Trash2, Pencil,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const STATUS_META = {
  not_started: { label: "Not started", color: "bg-slate-50 text-slate-700" },
  in_progress: { label: "In progress", color: "bg-sky-50 text-sky-700" },
  on_track:    { label: "On track",    color: "bg-emerald-50 text-emerald-700" },
  at_risk:     { label: "At risk",     color: "bg-amber-50 text-amber-700" },
  achieved:    { label: "Achieved",    color: "bg-emerald-100 text-emerald-800" },
  missed:      { label: "Missed",      color: "bg-rose-50 text-rose-700" },
  cancelled:   { label: "Cancelled",   color: "bg-slate-100 text-slate-500" },
};
const STATUS_COLORS = ["#94a3b8", "#0ea5e9", "#10b981", "#f59e0b", "#059669", "#ef4444", "#cbd5e1"];

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : ""; }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString() : ""; }
function StatusPill({ s }) {
  const m = STATUS_META[s] || STATUS_META.not_started;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${m.color}`}>{m.label}</span>;
}

function KPIDetail({ id, onBack, onChanged, user }) {
  const [k, setK] = useState(null);
  const [curVal, setCurVal] = useState("");
  const [note, setNote] = useState("");
  const [statusOverride, setStatusOverride] = useState("");
  const [busy, setBusy] = useState(false);
  // Progress-entry edit state — id of the entry being edited + its draft fields
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editDraft, setEditDraft] = useState({ current_value: "", status: "", note: "" });

  const load = async () => {
    try {
      // Explicit force-refresh: fetch fresh KPI (with all progress entries)
      // and replace state atomically. The response object is a fresh object
      // reference so React re-renders every derived view (progress bar,
      // over-time chart, timeline). Cache-busting query param defeats any
      // conditional-GET / browser-cache surprises.
      const r = await api.get(`/kpis/${id}`, { params: { _t: Date.now() } });
      setK(r.data);
    } catch (e) { toast.error(formatApiError(e)); onBack(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addProgress = async () => {
    if (curVal === "") return toast.error("Current value is required");
    setBusy(true);
    try {
      await api.post(`/kpis/${id}/progress`, {
        current_value: Number(curVal),
        status: statusOverride || null,
        note: note.trim() || null,
      });
      toast.success("Progress logged"); setCurVal(""); setNote(""); setStatusOverride("");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try { await api.patch(`/kpis/${id}`, { status: newStatus }); toast.success("Status updated"); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const removeKpi = async () => {
    if (!window.confirm("Delete this KPI? Progress entries will also be removed.")) return;
    try { await api.delete(`/kpis/${id}`); toast.success("KPI deleted"); onBack(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Progress-entry edit helpers ----
  const startEditEntry = (p) => {
    setEditingEntryId(p.id);
    setEditDraft({
      current_value: String(p.current_value ?? ""),
      status: p.status || "",
      note: p.note || "",
    });
  };
  const cancelEditEntry = () => { setEditingEntryId(null); setEditDraft({ current_value: "", status: "", note: "" }); };
  const saveEditEntry = async () => {
    if (editDraft.current_value === "") { toast.error("Current value is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/kpis/${id}/progress/${editingEntryId}`, {
        current_value: Number(editDraft.current_value),
        status: editDraft.status || null,
        note: editDraft.note.trim() || null,
      });
      toast.success("Progress entry updated");
      cancelEditEntry();
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this progress entry? The KPI's current value will be recalculated.")) return;
    try {
      await api.delete(`/kpis/${id}/progress/${entryId}`);
      toast.success("Progress entry removed");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!k) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const pct = k.target_value > 0 ? Math.min(100, Math.round((k.current_value / k.target_value) * 100)) : 0;
  const isOwnerTier = user?.role === "school_owner" || user?.role === "sub_owner";
  const isCreator = k.created_by_user_id === user?.id;
  // Granular KPI-level rights — mirror backend `_can_edit_kpi` / `_can_delete_kpi`
  // (school_owner always; sub_owner/branch_admin with the matching key, or
  // legacy `kpis_manage`). Fixes the bug where branch_admins with edit/delete
  // perms weren't seeing the KPI-level controls when viewing KPIs on other
  // branches they had access to.
  const canEditKpi = isOwnerTier || isCreator
    || (user?.role === "sub_owner" && (user.permissions || []).some((p) => p === "kpis_edit" || p === "kpis_manage"))
    || (user?.role === "branch_admin" && ((user.permissions || {}).kpis_edit || (user.permissions || {}).kpis_manage));
  const canDeleteKpi = isOwnerTier || isCreator
    || (user?.role === "sub_owner" && (user.permissions || []).some((p) => p === "kpis_delete" || p === "kpis_manage"))
    || (user?.role === "branch_admin" && ((user.permissions || {}).kpis_delete || (user.permissions || {}).kpis_manage));

  // Build a progress-over-time series for the chart
  const series = [...(k.progress || [])].reverse().map((p, i) => ({ idx: i + 1, value: p.current_value, when: new Date(p.created_at).toLocaleDateString() }));

  return (
    <div className="space-y-5" data-testid="kpi-detail">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline">&larr; Back to KPIs</button>
      <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">Deadline {fmtDate(k.deadline)} • {k.target_kind} • by {k.created_by_name}</div>
            <h2 className="font-heading text-2xl font-bold">{k.title}</h2>
            <div className="text-xs text-slate-500 mt-0.5">Targets: {(k.target_names || []).join(", ")}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill s={k.status} />
            {canDeleteKpi && (
              <button onClick={removeKpi} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-full" title="Delete KPI" data-testid="kpi-delete-btn"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>
        {k.description && <p className="whitespace-pre-wrap text-sm text-slate-700">{k.description}</p>}

        <div className="mt-2">
          <div className="flex items-center justify-between text-sm font-semibold">
            <span>{k.current_value} / {k.target_value} {k.unit || ""}</span>
            <span className="text-slate-500">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2.5 mt-1.5" />
        </div>

        {canEditKpi && (
          <div className="flex flex-wrap gap-2 pt-2" data-testid="kpi-status-buttons">
            {["in_progress", "on_track", "at_risk", "achieved", "missed", "cancelled"].map((s) => (
              <button key={s} onClick={() => changeStatus(s)} disabled={busy || k.status === s} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-50" data-testid={`kpi-status-btn-${s}`}>{STATUS_META[s].label}</button>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-3" data-testid="kpi-progress-form">
        <h3 className="font-heading font-bold">Log progress</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Current value</Label>
            <Input type="number" min="0" step="0.01" value={curVal} onChange={(e) => setCurVal(e.target.value)} data-testid="kpi-progress-value" />
          </div>
          <div>
            <Label>Set status (optional)</Label>
            <Select value={statusOverride} onValueChange={setStatusOverride}>
              <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
              <SelectContent>
                {["in_progress", "on_track", "at_risk", "achieved"].map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Textarea rows={3} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="kpi-progress-note" />
        <Button onClick={addProgress} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="kpi-progress-submit">Log progress</Button>
      </Card>

      {series.length > 0 && (
        <Card className="p-6 rounded-3xl border-[#2563EB]/10" data-testid="kpi-progress-chart">
          <h3 className="font-heading font-bold mb-3">Progress over time</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="when" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="p-6 rounded-3xl border-[#2563EB]/10">
        <h3 className="font-heading font-bold mb-3">Progress timeline</h3>
        {(k.progress || []).length === 0 ? (
          <p className="text-sm text-slate-500">No progress entries yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {k.progress.map((p) => {
              const isEditing = editingEntryId === p.id;
              // Entry-level edit rights: creator of the entry, school_owner,
              // OR sub_owner / branch_admin with `kpis_edit` (`kpis_manage` legacy).
              const canEditThis =
                p.user_id === user?.id
                || user?.role === "school_owner"
                || (user?.role === "sub_owner" && (user.permissions || []).some((x) => x === "kpis_edit" || x === "kpis_manage"))
                || (user?.role === "branch_admin" && ((user.permissions || {}).kpis_edit || (user.permissions || {}).kpis_manage));
              return (
                <div key={p.id} className="py-3 flex items-start gap-3 text-sm" data-testid={`kpi-progress-entry-${p.id}`}>
                  <TrendingUp className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="grid sm:grid-cols-3 gap-2">
                          <Input
                            type="number" min="0" step="0.01"
                            value={editDraft.current_value}
                            onChange={(e) => setEditDraft((d) => ({ ...d, current_value: e.target.value }))}
                            placeholder="Current value"
                            data-testid={`kpi-progress-edit-value-${p.id}`}
                          />
                          <Select
                            value={editDraft.status}
                            onValueChange={(v) => setEditDraft((d) => ({ ...d, status: v }))}
                          >
                            <SelectTrigger data-testid={`kpi-progress-edit-status-${p.id}`}>
                              <SelectValue placeholder="Keep as-is" />
                            </SelectTrigger>
                            <SelectContent>
                              {["in_progress", "on_track", "at_risk", "achieved"].map((s) => (
                                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea
                          rows={2}
                          value={editDraft.note}
                          onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                          placeholder="Note (optional)"
                          data-testid={`kpi-progress-edit-note-${p.id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm" onClick={saveEditEntry} disabled={busy}
                            className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]"
                            data-testid={`kpi-progress-edit-save-${p.id}`}
                          >Save</Button>
                          <Button
                            size="sm" variant="ghost" onClick={cancelEditEntry}
                            className="rounded-full"
                            data-testid={`kpi-progress-edit-cancel-${p.id}`}
                          >Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="font-semibold">{p.current_value} {k.unit || ""}{p.status ? ` → ${STATUS_META[p.status]?.label}` : ""}</div>
                        {p.note && <p className="text-slate-600 mt-0.5 whitespace-pre-wrap">{p.note}</p>}
                        <div className="text-xs text-slate-400 mt-0.5">
                          {p.user_name} • {fmt(p.created_at)}
                          {p.edited_at && (
                            <span className="ml-1 italic">· edited {fmt(p.edited_at)}{p.edited_by_name && p.edited_by_name !== p.user_name ? ` by ${p.edited_by_name}` : ""}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!isEditing && canEditThis && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEditEntry(p)}
                        className="text-slate-500 hover:bg-slate-100 p-1.5 rounded-full"
                        title="Edit this entry"
                        data-testid={`kpi-progress-edit-btn-${p.id}`}
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => deleteEntry(p.id)}
                        className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-full"
                        title="Delete this entry"
                        data-testid={`kpi-progress-delete-btn-${p.id}`}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function KPICreate({ onBack, onCreated, user }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [deadline, setDeadline] = useState("");
  const [kind, setKind] = useState("school");
  const [branchIds, setBranchIds] = useState([]);
  const [empIds, setEmpIds] = useState([]);
  const [branches, setBranches] = useState([]);
  // employees grouped by branch_id
  const [empByBranch, setEmpByBranch] = useState({});
  const [empSearch, setEmpSearch] = useState("");
  const [openBranches, setOpenBranches] = useState({}); // branch_id -> bool
  const [busy, setBusy] = useState(false);

  // Load approved branches + all employees grouped by branch
  useEffect(() => {
    (async () => {
      let approved = [];
      try {
        const r = await api.get("/branches/my");
        approved = (r.data || []).filter((b) => b.status === "approved");
        setBranches(approved);
      } catch { /* ignore */ }
      const initOpen = {};
      const map = {};
      await Promise.all(approved.map(async (b) => {
        try {
          const r = await api.get(`/employees`, { params: { branch_id: b.id } });
          map[b.id] = r.data || [];
          initOpen[b.id] = false;
        } catch { map[b.id] = []; }
      }));
      setEmpByBranch(map);
      setOpenBranches(initOpen);
    })();
  }, []);

  const toggleEmp = (id) => setEmpIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const toggleBranchOpen = (bid) => setOpenBranches((o) => ({ ...o, [bid]: !o[bid] }));
  const selectAllInBranch = (bid, all) => {
    const ids = (empByBranch[bid] || []).map((e) => e.id);
    setEmpIds((arr) => {
      const set = new Set(arr);
      if (all) ids.forEach((i) => set.add(i)); else ids.forEach((i) => set.delete(i));
      return [...set];
    });
  };

  const filteredEmps = (bid) => {
    const q = empSearch.trim().toLowerCase();
    const list = empByBranch[bid] || [];
    if (!q) return list;
    return list.filter((e) => (e.name || e.full_name || "").toLowerCase().includes(q));
  };

  const submit = async () => {
    if (!title.trim() || !targetValue || !deadline) return toast.error("Title, target value and deadline are required");
    if (kind === "branches" && branchIds.length === 0) return toast.error("Pick at least one branch");
    if (kind === "employees" && empIds.length === 0) return toast.error("Pick at least one employee");
    setBusy(true);
    try {
      await api.post("/kpis", {
        title: title.trim(), description: description.trim() || null, unit: unit.trim() || null,
        target_value: Number(targetValue), deadline, target_kind: kind,
        branch_ids: kind === "branches" ? branchIds : [],
        employee_ids: empIds,
      });
      toast.success("KPI created"); onCreated();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const showEmployeePicker = kind !== "branches"; // school-wide or employees
  const employeesLabel =
    kind === "employees"
      ? "Select employees (required)"
      : "Narrow to specific employees (optional)";

  return (
    <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-4" data-testid="kpi-create">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline self-start">&larr; Back</button>
      <h2 className="font-heading text-2xl font-bold">Create a KPI</h2>
      <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="kpi-title" /></div>
      <div><Label>Description (optional)</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Target value</Label><Input type="number" min="0" step="0.01" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} data-testid="kpi-target-value" /></div>
        <div><Label>Unit (optional)</Label><Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g., enrollments" /></div>
        <div><Label>Deadline</Label><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="kpi-deadline" /></div>
      </div>
      <div>
        <Label>Apply to</Label>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="kpi-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="school">School-wide</SelectItem>
            <SelectItem value="branches">Specific branches</SelectItem>
            <SelectItem value="employees">Specific employees</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {kind === "branches" && (
        <div>
          <Label>Branches</Label>
          <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto mt-1.5">
            {branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded-2xl px-3 py-2 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={branchIds.includes(b.id)} onChange={() => setBranchIds((arr) => arr.includes(b.id) ? arr.filter((x) => x !== b.id) : [...arr, b.id])} />
                {b.name}
              </label>
            ))}
          </div>
        </div>
      )}
      {showEmployeePicker && (
        <div className="space-y-2" data-testid="kpi-employee-picker">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="m-0">{employeesLabel}</Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{empIds.length} selected</span>
              {empIds.length > 0 && (
                <button type="button" onClick={() => setEmpIds([])} className="text-xs text-rose-500 hover:underline">Clear all</button>
              )}
            </div>
          </div>
          <Input
            placeholder="Search employees…"
            value={empSearch}
            onChange={(e) => setEmpSearch(e.target.value)}
            data-testid="kpi-employee-search"
          />
          <div className="max-h-72 overflow-y-auto space-y-2 border border-slate-200 rounded-2xl p-2">
            {branches.length === 0 && <p className="text-xs text-slate-500 p-3">No approved branches yet.</p>}
            {branches.map((b) => {
              const list = filteredEmps(b.id);
              const allIds = (empByBranch[b.id] || []).map((e) => e.id);
              const selectedHere = allIds.filter((id) => empIds.includes(id)).length;
              const allSelected = selectedHere > 0 && selectedHere === allIds.length;
              const isOpen = openBranches[b.id] ?? (empSearch.trim().length > 0);
              return (
                <div key={b.id} className="border border-slate-200 rounded-xl bg-white">
                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => toggleBranchOpen(b.id)}>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <span>{isOpen ? "▾" : "▸"}</span>
                      <span>{b.name}</span>
                      <span className="text-xs text-slate-400">({list.length}{selectedHere ? ` · ${selectedHere} selected` : ""})</span>
                    </div>
                    {allIds.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); selectAllInBranch(b.id, !allSelected); }}
                        className="text-xs text-[#1D4ED8] hover:underline"
                      >
                        {allSelected ? "Unselect all" : "Select all"}
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="px-3 pb-2 grid sm:grid-cols-2 gap-1.5">
                      {list.length === 0 && <p className="text-xs text-slate-400 py-1.5 col-span-2">No employees match.</p>}
                      {list.map((e) => (
                        <label key={e.id} className="flex items-center gap-2 text-sm border border-slate-100 rounded-xl px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={empIds.includes(e.id)} onChange={() => toggleEmp(e.id)} />
                          <span className="truncate">{e.name || e.full_name || "—"}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Button onClick={submit} disabled={busy} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] w-full sm:w-auto" data-testid="kpi-submit-btn">
        <Target className="w-4 h-4 mr-1.5" /> Create KPI
      </Button>
    </Card>
  );
}

function ReportPanel({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  // Re-fetch whenever `refreshKey` changes so the pie + bar chart update
  // instantly after creating, editing, deleting, or restatusing a KPI.
  useEffect(() => { (async () => {
    try { setData((await api.get("/kpis/report")).data); } catch { /* ignore */ }
  })(); }, [refreshKey]);
  if (!data) return null;
  const status = Object.entries(data.status_counts || {}).map(([name, value]) => ({ name: STATUS_META[name]?.label || name, value }));
  const byBranch = data.by_branch || [];
  if (status.length === 0 && byBranch.length === 0) return null;
  return (
    <Card className="p-6 rounded-3xl border-[#2563EB]/10" data-testid="kpi-report">
      <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-slate-500" /><h3 className="font-heading font-bold">Report</h3></div>
      <div className="grid md:grid-cols-2 gap-6">
        {status.length > 0 && (
          <div style={{ width: "100%", height: 240 }}>
            <p className="text-xs uppercase font-semibold text-slate-500 mb-2">By status</p>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={status} dataKey="value" nameKey="name" outerRadius={80} label>
                  {status.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {byBranch.length > 0 && (
          <div style={{ width: "100%", height: 240 }}>
            <p className="text-xs uppercase font-semibold text-slate-500 mb-2">By branch</p>
            <ResponsiveContainer>
              {/* Single stacked bar per branch summing to `total`.
                  Segments = achieved (green) + missed (red) + other (gray,
                  = in-progress/on-track/at-risk/not-started/cancelled). */}
              <BarChart data={byBranch.map((b) => ({
                ...b,
                other: Math.max(0, (b.total || 0) - (b.achieved || 0) - (b.missed || 0)),
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="achieved" stackId="a" fill="#10b981" name="Achieved" />
                <Bar dataKey="missed"   stackId="a" fill="#ef4444" name="Missed"   />
                <Bar dataKey="other"    stackId="a" fill="#94a3b8" name="In progress" opacity={0.5} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function KPIsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  // Tabs: "" = All, "__school__" = School-wide, "<branch_id>" = that branch
  const [activeTab, setActiveTab] = useState("");
  const [branches, setBranches] = useState([]);
  const [view, setView] = useState({ mode: "list" });
  // Bumped after every mutation so ReportPanel refetches its charts.
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const bumpReport = React.useCallback(() => setReportRefreshKey((n) => n + 1), []);
  const navigate = useNavigate();

  const canCreate = user?.role === "school_owner"
    || (user?.role === "sub_owner" && (user.permissions || []).some((p) => p === "kpis_create" || p === "kpis_manage"))
    || (user?.role === "branch_admin" && ((user.permissions || {}).kpis_create || (user.permissions || {}).kpis_manage));

  // Show tabs to everyone who can see multiple branches (owners always; branch
  // admins with multi-branch assignments; sub-owners on same rules as owners).
  const showTabs = user?.role === "school_owner" || user?.role === "sub_owner" || user?.role === "branch_admin";

  useEffect(() => {
    if (!showTabs) return;
    (async () => {
      try {
        const r = await api.get("/branches/my");
        setBranches((r.data || []).filter((b) => b.status === "approved"));
      } catch { /* ignore */ }
    })();
  }, [showTabs]);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch ALL KPIs the user can see; tab filtering happens client-side so
      // switching tabs is instant.
      const r = await api.get("/kpis", { params: {
        status: statusFilter || undefined,
      } });
      setItems(r.data?.items || []);
      // Refresh the pie + bar charts every time the list reloads so the
      // Report panel stays in sync with mutations (create / edit / delete /
      // status change). Fixes the classic "bar graph is not updating" bug.
      bumpReport();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  useEffect(() => {
    const m = window.location.pathname.match(/\/kpis\/([^/]+)$/);
    if (m) setView({ mode: "detail", id: m[1] });
  // eslint-disable-next-line
  }, []);

  const open = (id) => { setView({ mode: "detail", id }); navigate(`/kpis/${id}`); };
  const back = () => { setView({ mode: "list" }); navigate("/kpis"); load(); };

  // Filter items to the selected tab:
  //   "" (All)          → everything
  //   "__school__"      → only school-wide KPIs (target_kind === "school")
  //   "<branch_id>"     → KPIs targeting that branch (direct via branch_ids or
  //                       indirect via employees whose home branch matches)
  const filteredItems = React.useMemo(() => {
    if (!activeTab) return items;
    if (activeTab === "__school__") return items.filter((k) => k.target_kind === "school");
    return items.filter((k) => (k.all_branch_ids || k.branch_ids || []).includes(activeTab));
  }, [items, activeTab]);

  // Per-tab counts so each tab shows how many KPIs it holds
  const tabCounts = React.useMemo(() => {
    const counts = { all: items.length, school: 0 };
    branches.forEach((b) => { counts[b.id] = 0; });
    items.forEach((k) => {
      if (k.target_kind === "school") counts.school += 1;
      (k.all_branch_ids || k.branch_ids || []).forEach((bid) => {
        if (bid in counts) counts[bid] += 1;
      });
    });
    return counts;
  }, [items, branches]);

  return (
    <>
      <div className="space-y-5" data-testid="kpis-page">
        {view.mode === "list" && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs text-slate-400 uppercase font-heading">Performance</p>
                <h1 className="font-heading text-3xl font-bold">KPI Targets</h1>
                <p className="text-sm text-brand-inkMuted mt-1">Set, track, and report on key performance targets.</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="kpi-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {canCreate && (
                  <Button onClick={() => setView({ mode: "create" })} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="kpi-new-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> New KPI
                  </Button>
                )}
              </div>
            </div>
            <ReportPanel refreshKey={reportRefreshKey} />

            {/* Tabs: All · School-wide · one per branch. Client-side filter so
                switching tabs is instant. Rendered as scroll-x on mobile. */}
            {showTabs && (
              <div className="border-b border-slate-200 -mx-1 px-1 overflow-x-auto" data-testid="kpi-tabs">
                <div className="flex items-center gap-1 min-w-max">
                  {[
                    { key: "", label: "All", count: tabCounts.all },
                    { key: "__school__", label: "School-wide", count: tabCounts.school },
                    // Sort branches alphabetically so the tab order is stable
                    // and matches ordering used elsewhere in the app.
                    ...[...branches].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((b) => ({ key: b.id, label: b.name, count: tabCounts[b.id] || 0 })),
                  ].map((t) => {
                    const active = activeTab === t.key;
                    return (
                      <button
                        key={t.key || "all"}
                        type="button"
                        onClick={() => setActiveTab(t.key)}
                        data-testid={`kpi-tab-${t.key || "all"}`}
                        className={`px-4 py-2 text-sm font-heading font-semibold border-b-2 transition-colors whitespace-nowrap ${
                          active
                            ? "border-[#2563EB] text-[#2563EB]"
                            : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {t.label}
                        <span className={`ml-1.5 text-xs ${active ? "text-[#2563EB]" : "text-slate-400"}`}>({t.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {loading && <p className="text-center text-slate-500 py-8">Loading…</p>}
            {!loading && filteredItems.length === 0 && (
              <Card className="p-10 rounded-3xl text-center">
                <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="font-heading font-bold">No KPIs {activeTab ? "in this tab" : "yet"}</p>
                <p className="text-sm text-slate-500 mt-1">{canCreate ? "Tap New KPI to create one." : "Nothing to track yet."}</p>
              </Card>
            )}
            <div className="space-y-3">
              {filteredItems.map((k) => {
                const pct = k.target_value > 0 ? Math.min(100, Math.round((k.current_value / k.target_value) * 100)) : 0;
                // Chip list: for employee KPIs show employee names; for branch
                // KPIs show branch names; for school-wide show a single pill.
                const isEmpKpi = k.target_kind === "employees" && (k.employee_names || []).length > 0;
                const chipLabels = k.target_kind === "school"
                  ? ["School-wide"]
                  : (isEmpKpi ? (k.employee_names || []) : (k.branch_names || []));
                const chipTone = k.target_kind === "school"
                  ? "bg-[#DBEAFE] text-[#1D4ED8]"                    // school = blue
                  : isEmpKpi
                    ? "bg-amber-100 text-amber-800"                  // employees = amber
                    : "bg-emerald-100 text-emerald-800";             // branches = emerald
                const shown = chipLabels.slice(0, 2);
                const extra = chipLabels.length - shown.length;
                return (
                  <button key={k.id} onClick={() => open(k.id)} className="w-full text-left bg-white border border-[#2563EB]/10 rounded-3xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`kpi-row-${k.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-slate-500">Deadline {fmtDate(k.deadline)}</div>
                        <div className="font-heading font-bold text-foreground mt-0.5 truncate">{k.title}</div>
                        {chipLabels.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`kpi-chips-${k.id}`}>
                            {shown.map((name, i) => (
                              <span
                                key={`${name}-${i}`}
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-heading font-semibold ${chipTone}`}
                              >
                                {name}
                              </span>
                            ))}
                            {extra > 0 && (
                              <span
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-heading font-semibold bg-slate-100 text-slate-600"
                                title={chipLabels.slice(2).join(", ")}
                              >
                                +{extra} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <StatusPill s={k.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                      <span>{k.current_value} / {k.target_value} {k.unit || ""}</span>
                      <span>{pct}%</span>
                    </div>
                    <Progress value={pct} className="h-2 mt-1" />
                  </button>
                );
              })}
            </div>
          </>
        )}
        {view.mode === "create" && <KPICreate onBack={back} onCreated={back} user={user} />}
        {view.mode === "detail" && <KPIDetail id={view.id} onBack={back} onChanged={load} user={user} />}
      </div>
    </>
  );
}
